import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import DisclaimerBanner from '../components/DisclaimerBanner';
import { StrategySelector, GenerateButton, RecommendationResult } from '../components/recommendations/RecommendationWidgets';
import { toast } from 'sonner';
import { 
  Sparkles, 
  Shield, 
  Zap, 
  Target,
  Clock,
  AlertCircle,
  Lock,
  Crown,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';

const strategies = [
  {
    id: 'conservative',
    name: 'Conservadora',
    icon: Shield,
    description: 'Basada en los números más frecuentes',
    color: 'green',
    minPlan: 'basic'
  },
  {
    id: 'balanced',
    name: 'Balanceada',
    icon: Target,
    description: 'Combina frecuencia y mayor peso a los resultados recientes',
    color: 'blue',
    minPlan: 'premium'
  },
  {
    id: 'risky',
    name: 'Arriesgada',
    icon: Zap,
    description: 'Menor frecuencia observada; el retraso no aumenta la probabilidad',
    color: 'pink',
    minPlan: 'vip'
  }
];

const Recommendations = () => {
  const { token, user } = useAuth();
  const [lotteries, setLotteries] = useState([]);
  const [selectedLottery, setSelectedLottery] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState('conservative');
  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [lotteriesRes, schedulesRes, subRes, usageRes] = await Promise.all([
        axios.get(`${API}/lotteries`),
        axios.get(`${API}/schedule/next`),
        axios.get(`${API}/subscription/subscription/current`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { subscription: null } })),
        axios.get(`${API}/recommendations/usage`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      
      setLotteries(lotteriesRes.data);
      setSchedules(schedulesRes.data);
      setSubscription(subRes.data?.subscription);
      setUsage(usageRes.data);
      
      if (lotteriesRes.data.length > 0) {
        setSelectedLottery(lotteriesRes.data[0]);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const generateRecommendation = async () => {
    if (!selectedLottery) {
      toast.error('Selecciona una lotería');
      return;
    }

    if (!subscription && !user?.is_admin) {
      toast.error('Necesitas una suscripción activa');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/recommendations`,
        {
          lottery_id: selectedLottery.id,
          strategy: selectedStrategy
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setRecommendation(response.data);
      toast.success('¡Recomendación generada!');
      
      // Refresh usage
      try {
        const usageRes = await axios.get(`${API}/recommendations/usage`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUsage(usageRes.data);
      } catch {
        setUsage(null);
        toast.info('Recomendación guardada; no se pudo actualizar el cupo mostrado.');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Error al generar recomendación');
    } finally {
      setLoading(false);
    }
  };

  const getLotterySchedule = () => {
    if (!selectedLottery) return null;
    return schedules.find(s => s.lottery_id === selectedLottery.id);
  };

  const formatTimeRemaining = (isoDate) => {
    const now = new Date();
    const draw = new Date(isoDate);
    const diff = draw - now;
    
    if (diff < 0) return 'Sorteo cerrado';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} día${days > 1 ? 's' : ''} ${hours % 24}h ${minutes}m`;
    }
    
    return `${hours}h ${minutes}m`;
  };

  const isStrategyAvailable = (strategyId) => {
    if (user?.is_admin) return true;
    if (!subscription) return false;
    
    const planId = subscription.plan_id;
    const strategy = strategies.find(s => s.id === strategyId);
    
    if (strategy.minPlan === 'basic') return true;
    if (strategy.minPlan === 'premium') return ['premium', 'vip'].includes(planId);
    if (strategy.minPlan === 'vip') return planId === 'vip';
    
    return false;
  };

  const canGenerateMore = () => {
    if (user?.is_admin) return true;
    if (!subscription) return false;
    return !usage || usage.unlimited || usage.used < usage.limit;
  };

  const schedule = getLotterySchedule();
  const hasSubscription = subscription || user?.is_admin;

  return (
    <div className="min-h-screen bg-slate-950 pb-20">
      <Navbar />
      
      <main className="pt-20 px-4 max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center mx-auto mb-4 neon-glow-green">
            <Sparkles size={32} className="text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" data-testid="recommendations-title">
            Motor de Recomendaciones
          </h1>
          <p className="text-slate-400">Genera números basados en análisis estadístico</p>
        </div>

        {/* Subscription Warning */}
        {!hasSubscription && (
          <Card className="glass-card border-amber-500/30 mb-8" data-testid="subscription-warning">
            <CardContent className="py-6">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Lock size={24} className="text-amber-400" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-white font-bold mb-1">Suscripción Requerida</h3>
                  <p className="text-slate-400 text-sm">
                    Necesitas una suscripción activa para generar recomendaciones de números.
                  </p>
                </div>
                <Link to="/pricing">
                  <Button className="bg-amber-600 hover:bg-amber-500" data-testid="upgrade-btn">
                    <Crown size={18} className="mr-2" />
                    Ver Planes
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Usage Indicator */}
        {hasSubscription && usage && !usage.unlimited && (
          <Card className="glass-card mb-6" data-testid="usage-card">
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-400 text-sm">Recomendaciones de hoy</span>
                <span className="text-white font-mono">
                  {usage.used} / {usage.limit}
                </span>
              </div>
              <Progress 
                value={usage.limit > 0 ? (usage.used / usage.limit) * 100 : 0} 
                className="h-2 bg-slate-800"
              />
              {usage.used >= usage.limit && (
                <p className="text-amber-400 text-sm mt-2">
                  Has alcanzado tu límite diario. 
                  <Link to="/pricing" className="underline ml-1">Mejora tu plan</Link> para más recomendaciones.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Configuration */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Lottery Selection */}
          <Card className="glass-card" data-testid="lottery-selection-card">
            <CardHeader>
              <CardTitle className="text-white text-lg">Seleccionar Lotería</CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={selectedLottery?.id}
                onValueChange={(value) => {
                  const lottery = lotteries.find(l => l.id === value);
                  setSelectedLottery(lottery);
                  setRecommendation(null);
                }}
              >
                <SelectTrigger className="w-full bg-slate-800 border-slate-700 text-white" data-testid="rec-lottery-selector">
                  <SelectValue placeholder="Seleccionar lotería" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {lotteries.map((lottery) => (
                    <SelectItem 
                      key={lottery.id} 
                      value={lottery.id}
                      className="text-white hover:bg-slate-800 focus:bg-slate-800"
                    >
                      {lottery.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {schedule && (
                <div className={`mt-4 p-3 rounded-lg ${schedule.is_open ? 'bg-green-900/30 border border-green-500/30' : 'bg-slate-800/50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock size={16} className={schedule.is_open ? 'text-green-400' : 'text-slate-400'} />
                    <span className={`text-sm font-medium ${schedule.is_open ? 'text-green-400' : 'text-slate-400'}`}>
                      {schedule.is_open ? 'Sorteo Abierto' : 'Próximo Sorteo'}
                    </span>
                  </div>
                  <p className="text-white font-mono">{formatTimeRemaining(schedule.next_draw)}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Strategy Selection */}
          <StrategySelector
            strategies={strategies}
            selectedStrategy={selectedStrategy}
            setSelectedStrategy={setSelectedStrategy}
            setRecommendation={setRecommendation}
            isStrategyAvailable={isStrategyAvailable}
          />
        </div>

        {/* Generate Button */}
        <GenerateButton
          loading={loading}
          hasSubscription={hasSubscription}
          canGenerateMore={canGenerateMore()}
          onClick={generateRecommendation}
          disabled={loading || !selectedLottery || !hasSubscription || !canGenerateMore()}
        />

        {/* Recommendation Result */}
        {recommendation && (
          <RecommendationResult
            recommendation={recommendation}
            strategies={strategies}
            lotteryName={selectedLottery?.name}
          />
        )}
      </main>

      <DisclaimerBanner variant="fixed" />
    </div>
  );
};

export default Recommendations;
